import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationError } from "../../shared/validation.error";
import { LogService } from "../../shared/log.service";
import { TranslationConfig } from "../translation-config";
import { Void } from "../../shared/void";
import { UseCase } from "../../shared/use-case";
import { AddTranslationsToStoreUseCaseRequest } from "./add-translations-to-store.use-case-request";
import { LocalStorageTranslations, TranslationStore } from '../translation';

@Injectable({
    providedIn: 'root'
})
export class AddTranslationsToStoreUseCase extends UseCase<AddTranslationsToStoreUseCaseRequest, Void> {

    constructor(
        _logService: LogService,
        _httpClient: HttpClient,
        private _translationConfig: TranslationConfig
    ) {
        super(_logService, _httpClient, _translationConfig.enableUseCaseStatusLogs);
    }

    protected override validate(request: AddTranslationsToStoreUseCaseRequest) {
        if (!request.languageOfNewTranslations){
            new ValidationError("Language is not set for new translations!");
        }
        if (!request.newTranslations){
            new ValidationError("Nothing to add to the translations!");
        }
        if (!request.nameOfLocalStorage){
            new ValidationError("Translations has no available local storage!");
        }
    }

    protected override executeBusinessLogic(request: AddTranslationsToStoreUseCaseRequest): void {
        if (this._isLocalStorageVariableEmpty(request.nameOfLocalStorage)) {
            this._initLocalStorageVariable(request.nameOfLocalStorage);
        }
        const localStorageTranslations: LocalStorageTranslations[] = this._getLocalStorageVariable(request.nameOfLocalStorage);
        const translationStoreforLanguage = this._getLanguageTranslationsFromLocalStorage(localStorageTranslations, request.languageOfNewTranslations);
        const mergedTranslations = { ...translationStoreforLanguage ,...request.newTranslations };
        localStorage.setItem(
            request.nameOfLocalStorage,
            JSON.stringify(this._updateTranslationsForLanguage(localStorageTranslations, mergedTranslations, request.languageOfNewTranslations))
        );
    }

    private _isLocalStorageVariableEmpty(nameOfLocalStorage: string): boolean {
        return localStorage.getItem(nameOfLocalStorage) === null;
    }

    private _initLocalStorageVariable(nameOfLocalStorage: string): LocalStorageTranslations[] {
        const initTranslations: LocalStorageTranslations[] = [];
        this._translationConfig.translatedLanguages.forEach((language) => {
            initTranslations.push({ language: language, translations: {} });
        });
        localStorage.setItem(nameOfLocalStorage, JSON.stringify(initTranslations));
        return initTranslations;
    }

    private _getLocalStorageVariable(nameOfLocalStorage: string): LocalStorageTranslations[] {
        const localStorageVariable = localStorage.getItem(nameOfLocalStorage);
        if (localStorageVariable) {
            return JSON.parse(localStorageVariable) as LocalStorageTranslations[];
        }
        return [];
    }

    private _getLanguageTranslationsFromLocalStorage(localStorageVariable: LocalStorageTranslations[], language: string): TranslationStore {
        let languageTranslations: TranslationStore = {};
        localStorageVariable.forEach((variable) => {
            if (variable.language == language) {
                languageTranslations = variable.translations as TranslationStore;
            }
        });
        return languageTranslations;
    }

    private _updateTranslationsForLanguage(
        localStorageTranslations: LocalStorageTranslations[],
        localStorageTranslationsForLanguage: TranslationStore,
        language: string
    ): LocalStorageTranslations[] {
        localStorageTranslations.forEach((localStorageTranslation: LocalStorageTranslations) => {
            if (localStorageTranslation.language === language) {
                localStorageTranslation.translations = localStorageTranslationsForLanguage;
            }
        });
        return localStorageTranslations;
    }

}